import { useEffect, useRef, useState } from 'react';
import {
    Button,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Skeleton,
    Box,
    TextField,
    Alert,
    Menu,
    MenuItem,
    ListItemText,
    ListItemIcon,
} from '@mui/material';
import {
    ExpandMore,
    SolarPower,
    Preview,
    Handyman,
    Refresh,
    Troubleshoot,
    Save,
    DownloadOutlined,
    RequestQuoteOutlined,
    Info,
    CropPortraitOutlined,
    CropLandscapeOutlined,
    ExpandMoreOutlined,
    FileDownloadOutlined,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { setNestedObjectValues, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import classNames from 'classnames';
import { centroid } from '@turf/turf';

import { formFieldBuilder } from 'src/common/formikUtils';
import { formValidationSchema, INITIAL_FORM_VALUES } from './formDefinitions';
import PVInput from 'src/features/pv-analysis/components/calculator/PVInput';
import { OptimizerMode, PVInputParams } from 'src/features/pv-analysis/pv-input-types';
import { Optimizer } from 'src/features/pv-analysis/pv-calculator.dto';
import SalesResult, { SALES_RESULT_WIDTH, SALES_RESULT_HEIGHT } from 'src/features/sales/SalesResult';
import SalesPrint from 'src/features/sales/SalesPrint';
import EnergyConsumptionComponents from 'src/features/sales/components/EnergyConsumptionComponents';
import Search from './components/Search';
import Map, { TakeScreenshotOptions } from './components/Map';
import styles from './PvAnalysisCreateEditPage.module.css';
import { admiPVAnalysisService } from './admi-pv-analysis.service';
import { admiPVCalculatorService } from './admi-pv-calculator.service';
import { Business, MapOutlined } from '@mui/icons-material';
import SelectedBuildings from './components/SelectedBuildings';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { calculatePvResults, loadPvAnalysisVariantInitialView } from './pv-analysis.slice';
import PVOptimizationInsights from 'src/features/pv-analysis/components/calculator/PVOptimizationInsights';
import { useNavigateBlocker } from 'src/common/navigateBlocker';
import { downloadFileFromBlob } from 'src/common/downloadFile';
import { CreatePVAnalysisVariantDto, SalesPrintVariant } from './pv-analysis.dto';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';
import { exportPvAnalysisToCSV } from './utils/exportToCsv';

export default function PvAnalysisCreateEditPage() {
    const { editedVariant, errorMessage, isCalculationLoading, supportedBuildingFunctions, costModels } =
        useAppSelector((state) => state.pvAnalysis);
    const {
        pvAnalysisId,
        pvAnalysisName,
        name,
        streetAddress,
        calculatorResults,
        buildings: selectedBuildings,
    } = editedVariant;
    const dispatch = useAppDispatch();

    const loader = useGoogleMapsApiLoader();
    const { t } = useTranslation();
    const navigate = useNavigate();
    let hasUnsavedChanges = true;
    useNavigateBlocker(t('Are you sure you want to leave this page? Changes will be lost.'), () => hasUnsavedChanges);

    const [screenshotOptions, setScreenshotOptions] = useState<TakeScreenshotOptions | null>(null);
    const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
    const [pvParams, setPvParams] = useState<PVInputParams>(editedVariant.pvParams);
    const [calculatorResultsDirty, setCalculatorResultsDirty] = useState<boolean>(false);
    const [salesPrintAnchorEl, setSalesPrintAnchorEl] = useState<HTMLElement | null>(null);

    const formik = useFormik({
        initialValues: INITIAL_FORM_VALUES,
        validationSchema: formValidationSchema,
        onSubmit: createPvAnalysis,
    });

    const formField = (name: string) => formFieldBuilder(formik, name);

    useEffect(() => {
        if (screenshotDataUrl) {
            URL.revokeObjectURL(screenshotDataUrl);
        }
        if (screenshotOptions) {
            setScreenshotDataUrl(URL.createObjectURL(screenshotOptions.blob));
        }
    }, [screenshotOptions]);

    // when the building selection changes, we need to revoke the screenshot data URL
    // because it's no longer valid for the new buildings
    useEffect(() => {
        if (screenshotDataUrl) {
            URL.revokeObjectURL(screenshotDataUrl);
        }
        setScreenshotDataUrl(null);
    }, [selectedBuildings]);

    useEffect(() => {
        if (errorMessage) {
            dispatch(showSnackbar({ message: errorMessage, severity: NotificationSeverity.Error }));
        }
    }, [errorMessage, dispatch]);

    // Initiate loading an existing PV analysis variant, if an ID is provided
    const { id } = useParams();
    useEffect(() => {
        dispatch(loadPvAnalysisVariantInitialView(id))
            .unwrap()
            .then(([variant]) => {
                if (!variant) {
                    return;
                }

                admiPVAnalysisService
                    .getPVAnalysisMapScreenshotBlob(variant.id)
                    .then((blob) => {
                        if (variant.mapScreenshot) {
                            setScreenshotOptions({
                                center: variant.mapScreenshot.center,
                                zoom: variant.mapScreenshot.zoom,
                                blob,
                            });
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        dispatch(
                            showSnackbar({
                                message: t('Map screenshot not available'),
                                severity: NotificationSeverity.Error,
                            }),
                        );
                    });
            })
            .catch((error) => {
                console.error(error);
                dispatch(
                    showSnackbar({
                        message: t(`Failed to load PV Analysis variant: ${JSON.stringify(error)}`),
                        severity: NotificationSeverity.Error,
                    }),
                );
            })
            .finally(() => {
                setCalculatorResultsDirty(false);
            });
    }, [id, dispatch, t]);

    useEffect(() => {
        formik.setValues({ pvAnalysisName, name, streetAddress });
    }, [pvAnalysisName, name, streetAddress]);

    useEffect(() => {
        setPvParams(editedVariant.pvParams);
    }, [editedVariant.pvParams]);

    useEffect(() => {
        setCalculatorResultsDirty(true);

        // Fill street address automatically, if it is not yet filled
        if (selectedBuildings.length > 0 && streetAddress.length === 0) {
            handleLoadStreetAddressFromMap();
        }
    }, [selectedBuildings]);

    function calculatePv() {
        if (selectedBuildings.length === 0) {
            dispatch(
                showSnackbar({
                    message: t('Please select at least one building.'),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        if (!pvParams.buildingFunctionCode) {
            dispatch(
                showSnackbar({
                    message: t('Please select a building function.'),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        if (pvParams.productionOptimizerMode === OptimizerMode.Manual && pvParams.targetKwp <= 0) {
            dispatch(
                showSnackbar({
                    message: t('The target generator power has to be greater than 0 kWp.'),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        // A heat pump profile with a zero COP used to reach the calculator and fail there with an
        // opaque server error, since the COP is the divisor that turns heat demand into electricity.
        if (admiPVCalculatorService.hasIncompleteHeatPumpProfile(pvParams.loadProfiles)) {
            dispatch(
                showSnackbar({
                    message: t(
                        'Please enter an annual heat demand and an annual COP greater than 0 for the heat pump.',
                    ),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        setCalculatorResultsDirty(false);
        dispatch(calculatePvResults({ pvParams, selectedBuildings }));
    }

    function buildCreatePVAnalysisVariantDTO(): CreatePVAnalysisVariantDto | null {
        if (!screenshotOptions) {
            dispatch(
                showSnackbar({
                    message: 'Please take a screenshot of the map first.',
                    severity: NotificationSeverity.Warning,
                }),
            );
            return null;
        }

        if (!calculatorResults) {
            dispatch(
                showSnackbar({
                    message: 'Please run the PV calculation first.',
                    severity: NotificationSeverity.Warning,
                }),
            );
            return null;
        }

        const values = formik.values;
        return {
            name: values.name,
            buildings: selectedBuildings,
            mapScreenshot: { center: screenshotOptions.center, zoom: screenshotOptions.zoom },
            mapScreenshotImageData: screenshotOptions.blob,
            calculatorParams: {
                optimizer: pvParams.optimizer,
                buildingFunctionCode: pvParams.buildingFunctionCode,
                fundingRate: pvParams.fundingRate,
                energyPrice: pvParams.energyPrice,
                loadProfiles: pvParams.loadProfiles,
                costModel: pvParams.costModel,
                targetKwp: pvParams.productionOptimizerMode === OptimizerMode.Manual ? pvParams.targetKwp : null,
                batteryCapacityFactor:
                    pvParams.batteryOptimizerMode === OptimizerMode.Manual ? pvParams.batteryKwpFactor : null,
                limitTo100Kwp: pvParams.constrainFullRoofTo100KWp,
            },
            calculatorOptimizedResult: admiPVAnalysisService.mapPvCalculatorDtoToAppDto(
                calculatorResults.optimizedResult,
            ),
            calculatorFullRoofResult: admiPVAnalysisService.mapPvCalculatorDtoToAppDto(
                calculatorResults.fullRoofResult,
            ),
            pvAnalysisId: editedVariant.pvAnalysisId,
            pvAnalysisName: values.pvAnalysisName,
            streetAddress: values.streetAddress,
        };
    }

    async function createPvAnalysis(values: typeof INITIAL_FORM_VALUES) {
        console.debug('createPvAnalysis', values);

        try {
            const createPVAnalysisDTO = buildCreatePVAnalysisVariantDTO();
            if (!createPVAnalysisDTO) {
                return;
            }

            await admiPVAnalysisService.createPVAnalysisVariant(createPVAnalysisDTO);

            dispatch(
                showSnackbar({
                    message: t('PV Analysis created successfully.'),
                    severity: NotificationSeverity.Success,
                }),
            );

            hasUnsavedChanges = false;
            navigate('/pv-analysis');
        } catch (error) {
            console.error(error);
            if (error instanceof AxiosError) {
                if (error.message[0]) {
                    dispatch(showSnackbar({ message: error.message[0], severity: NotificationSeverity.Error }));
                }
            }
        }
    }

    async function handleGenerateSalesPrintPreview(salesPrintVariant: SalesPrintVariant) {
        const validationErrors = await formik.validateForm();
        if (Object.keys(validationErrors).length > 0) {
            // Set all erroneous fields as touched to show validation errors
            formik.setTouched(setNestedObjectValues(validationErrors, true));

            dispatch(
                showSnackbar({
                    message: t('Please fix the form errors to continue.'),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        const createPVAnalysisDTO = buildCreatePVAnalysisVariantDTO();
        if (!createPVAnalysisDTO) {
            return;
        }

        // The backend requires complete day/year charts on both results,
        // which older calculator versions did not produce yet.
        const { calculatorOptimizedResult, calculatorFullRoofResult } = createPVAnalysisDTO;
        if (
            !calculatorOptimizedResult.chartsDay ||
            !calculatorOptimizedResult.chartsYear ||
            !calculatorFullRoofResult.chartsDay ||
            !calculatorFullRoofResult.chartsYear
        ) {
            dispatch(
                showSnackbar({
                    message: t(
                        'Sales print cannot be created, because the potential analysis was created with an old version of the PV calculator. Please re-calculate.',
                    ),
                    severity: NotificationSeverity.Error,
                }),
            );
            return;
        }

        try {
            const documentData = await admiPVAnalysisService.createSalesPrintPreview(
                createPVAnalysisDTO,
                salesPrintVariant,
            );
            downloadFileFromBlob(
                documentData,
                `Potenzialanalyse-${formik.values.pvAnalysisName}-${formik.values.streetAddress}.pdf`,
            );
        } catch (error) {
            console.error(error);
            // The interceptor rejects with the ProblemDetail object (or plain text)
            // for API errors, and with an AxiosError for network failures.
            let detail: string | null = null;
            if (typeof error === 'string') {
                detail = error;
            } else if (typeof error === 'object' && error !== null) {
                if ('detail' in error && error.detail) {
                    detail = String(error.detail);
                } else if ('message' in error && error.message) {
                    detail = String(error.message);
                }
            }
            dispatch(
                showSnackbar({
                    message: detail
                        ? t('Failed to generate the sales print: {{detail}}', { detail })
                        : t('Failed to generate the sales print. Please re-calculate and try again.'),
                    severity: NotificationSeverity.Error,
                }),
            );
        }
    }

    // Scroll to the PV input section when clicking on a data point in the optimization insights
    // to review the changes.
    const pvInputSectionAnchorRef = useRef<HTMLElement>(null);
    function handleClickResultGraphDatapoint(targetKwp: number, batteryCapacityFactor: number) {
        setPvParams({
            ...pvParams,
            optimizer: Optimizer.ROI,
            productionOptimizerMode: OptimizerMode.Manual,
            targetKwp,
            batteryOptimizerMode: OptimizerMode.Manual,
            batteryKwpFactor: batteryCapacityFactor,
        });
        setCalculatorResultsDirty(true);

        pvInputSectionAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    async function handleLoadStreetAddressFromMap() {
        const firstBuilding = selectedBuildings[0];
        if (!firstBuilding) {
            dispatch(
                showSnackbar({
                    message: t('You have to select a building first'),
                    severity: NotificationSeverity.Warning,
                }),
            );
            return;
        }

        // Prefer the centroid over the center to be less affected by outliers
        // since center effectively calculates the center of the most extreme coords (the bounding box)
        const center = centroid(firstBuilding.geometry);

        const { Geocoder } = await loader.importLibrary('geocoding');
        const geocoder = new Geocoder();
        // `geocoder.geocode` can resolve to undefined when the Google Maps API
        // failed to bootstrap cleanly (e.g. NotLoadingAPIFromGoogleMapsError),
        // so guard the response before iterating `results`.
        const response = await geocoder
            .geocode({
                location: { lng: center.geometry.coordinates[0], lat: center.geometry.coordinates[1] },
                language: 'de',
            })
            .catch(() => undefined);

        const bestWithFullAddress = (response?.results ?? [])
            .map((result) => {
                const streetNumberComponent = result.address_components.find((component) =>
                    component.types.includes('street_number'),
                );
                const routeComponent = result.address_components.find((component) => component.types.includes('route'));
                const localityComponent = result.address_components.find((component) =>
                    component.types.includes('locality'),
                );

                return streetNumberComponent && routeComponent && localityComponent
                    ? `${routeComponent.long_name} ${streetNumberComponent.long_name}, ${localityComponent.long_name}`
                    : undefined;
            })
            .find((result) => result !== undefined);

        if (!bestWithFullAddress) {
            dispatch(showSnackbar({ message: 'No address found', severity: NotificationSeverity.Warning }));
            return;
        }

        formik.setFieldValue('streetAddress', bestWithFullAddress);
    }

    function handleExportToCSV() {
        if (!calculatorResults) {
            console.error('Full roof result is required for export');
            return;
        }

        exportPvAnalysisToCSV({
            optimizedResult: calculatorResults.optimizedResult,
            fullRoofResult: calculatorResults.fullRoofResult,
            pvAnalysisName: pvAnalysisName,
            variantName: formik.values.name,
            optimizer: calculatorResults.optimizer,
            fundingRate: calculatorResults.fundingRate,
        });
    }

    return (
        <form>
            <Grid container direction="row" spacing={5}>
                <Grid size={12}>
                    <Typography variant="h4">{t('New Potential Analysis')}</Typography>
                </Grid>

                <Grid size={12}>
                    <div>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Handyman /> {t('General Information')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('PV Analysis Name')}
                                    disabled={!!pvAnalysisId}
                                    size="small"
                                    slotProps={{
                                        htmlInput: { maxLength: 35 },
                                    }} /* 35 chars is roughly the space we have in the sales print */
                                    {...formField('pvAnalysisName')}
                                />

                                <TextField
                                    fullWidth
                                    required
                                    sx={{ mt: 2 }}
                                    label={t('Variation Name')}
                                    size="small"
                                    {...formField('name')}
                                />

                                {editedVariant.author && (
                                    <>
                                        <Typography sx={{ mt: 2 }}>{t('Author')}</Typography>
                                        <Typography variant="body2">{editedVariant.author}</Typography>
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <MapOutlined /> {t('Search')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid size={12}>
                                    <Search />
                                </Grid>
                                <Grid size={12}>
                                    <Map takeScreenshot={setScreenshotOptions} />
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <RequestQuoteOutlined /> {t('Sales print')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Alert color="info" icon={<Info />}>
                                    {t(
                                        'The address is automatically filled from the selected buildings. Make sure to check it, it may not always be correct.',
                                    )}
                                </Alert>
                                <Box sx={{ display: 'flex', alignItems: 'end', mt: 1 }}>
                                    <TextField
                                        required
                                        sx={{ mt: 2, flexGrow: 1 }}
                                        label={t('Street Address (used in the sales print)')}
                                        size="small"
                                        {...formField('streetAddress')}
                                    />
                                    <Button
                                        size="large"
                                        variant="outlined"
                                        startIcon={<MapOutlined />}
                                        sx={{ ml: 1 }}
                                        onClick={handleLoadStreetAddressFromMap}
                                    >
                                        {t('Load from map')}
                                    </Button>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Business /> {t('Buildings')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid size={12}>
                                    <SelectedBuildings />
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <SolarPower /> {t('PV Calculator')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <span ref={pvInputSectionAnchorRef}></span>
                                <PVInput
                                    params={pvParams}
                                    supportedBuildingFunctions={supportedBuildingFunctions ?? []}
                                    costModels={costModels ?? []}
                                    onChange={(value) => {
                                        setPvParams(value);
                                        setCalculatorResultsDirty(true);
                                    }}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Preview /> {t('PV Analysis Result')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={calculatePv}
                                        startIcon={<Refresh />}
                                        disabled={!calculatorResultsDirty}
                                    >
                                        {t('(Re-)calculate')}
                                    </Button>
                                </Box>

                                {isCalculationLoading && (
                                    <Skeleton
                                        variant="rectangular"
                                        width={SALES_RESULT_WIDTH}
                                        height={SALES_RESULT_HEIGHT}
                                    />
                                )}

                                {!isCalculationLoading && (
                                    <>
                                        {calculatorResults?.optimizedResult && calculatorResults?.fullRoofResult && (
                                            <Box
                                                className={classNames({
                                                    [styles.result]: true,
                                                    [styles.resultDirty]: calculatorResultsDirty,
                                                })}
                                            >
                                                <SalesPrint
                                                    projectName={[
                                                        formik.values.pvAnalysisName,
                                                        formik.values.streetAddress,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')}
                                                    energyConsumption={
                                                        <EnergyConsumptionComponents
                                                            components={calculatorResults.optimizedResult.load_summary}
                                                            baseUsageEstimated={calculatorResults.baseUsageEstimated}
                                                        />
                                                    }
                                                    plot={
                                                        <>
                                                            {screenshotDataUrl && <img src={screenshotDataUrl} />}
                                                            {!screenshotDataUrl && (
                                                                <Typography variant="body2">
                                                                    {t('Please take a screenshot of the map first.')}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                >
                                                    <SalesResult
                                                        result={calculatorResults.optimizedResult}
                                                        fundingRate={calculatorResults.fundingRate}
                                                        optimizer={calculatorResults.optimizer}
                                                    />
                                                    <SalesResult
                                                        result={calculatorResults.fullRoofResult}
                                                        fundingRate={0}
                                                        optimizer={Optimizer.FULL_ROOF}
                                                    />
                                                </SalesPrint>

                                                <Button
                                                    variant="outlined"
                                                    sx={{ ml: 1 }}
                                                    onClick={(event) => setSalesPrintAnchorEl(event.currentTarget)}
                                                    startIcon={<DownloadOutlined />}
                                                >
                                                    {t('Sales print')}
                                                    <ExpandMoreOutlined />
                                                </Button>
                                                <Menu
                                                    anchorEl={salesPrintAnchorEl}
                                                    open={Boolean(salesPrintAnchorEl)}
                                                    onClose={() => setSalesPrintAnchorEl(null)}
                                                >
                                                    <MenuItem
                                                        onClick={() => {
                                                            handleGenerateSalesPrintPreview(SalesPrintVariant.PORTRAIT);
                                                            setSalesPrintAnchorEl(null);
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <CropPortraitOutlined />
                                                        </ListItemIcon>
                                                        <ListItemText>{t('Portrait format')}</ListItemText>
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            handleGenerateSalesPrintPreview(
                                                                SalesPrintVariant.LANDSCAPE,
                                                            );
                                                            setSalesPrintAnchorEl(null);
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <CropLandscapeOutlined />
                                                        </ListItemIcon>
                                                        <ListItemText>{t('Landscape format')}</ListItemText>
                                                    </MenuItem>
                                                </Menu>

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<FileDownloadOutlined />}
                                                    onClick={handleExportToCSV}
                                                    sx={{ ml: 1 }}
                                                >
                                                    {t('Export to CSV')}
                                                </Button>
                                            </Box>
                                        )}

                                        {!calculatorResults && (
                                            <Typography>{t('Please run the PV calculation first.')}</Typography>
                                        )}
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Troubleshoot /> {t('Optimization Insights')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {calculatorResults && (
                                    <PVOptimizationInsights
                                        result={calculatorResults.optimizedResult}
                                        onClickResultGraphDataPoint={handleClickResultGraphDatapoint}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </Grid>

                <Grid size={12}>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => formik.handleSubmit()}
                        loading={formik.isSubmitting}
                    >
                        {t('Save PV Analysis (Only for Ops)')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
}
